import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  VisibilityOutlined,
  DeleteOutline as DeleteOutlineIcon,
  ChevronRight as ChevronRightIcon,
  SearchOutlined,
  NotificationsNoneOutlined,
  DoneAllOutlined,
  FiberManualRecord as DotIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import SortTableHead, { TableColumnDefinition } from "../../components/SortTableHead";
import LightTablePagination from "../../components/tables/LightTablePagination";
import TableEmpty from "../../components/tables/TableEmpty";
import Loader from "../../components/Loader";
import ActionToast from "../../components/ActionToast";
import { NotificationMessage } from "../../models/models";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import useTranslation from "../../hooks/useTranslation";
import {
  AppNotification,
  NotificationHistoryQuery,
  NotificationHistoryState,
  NotificationHistorySortParameter,
  ReadFilter,
} from "../models/notificationModels";
import {
  fetchNotificationHistory,
  markHistoryNotificationAsRead,
  markAllHistoryAsRead,
  deleteHistoryNotification,
  changeHistoryQuery,
  clearHistoryError,
} from "../notificationHistorySlice";
import { fetchUnreadCount, markAsRead } from "../notificationsSlice";
import useDataRefresh from "../hooks/useDataRefresh";
import useVisibilityRefresh from "../../hooks/useVisibilityRefresh";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../themes/redesignTokens";
import PermissionGuard from "../../components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";

dayjs.extend(relativeTime);

// ===================================================================
//  DESIGN TOKENS
// ===================================================================

// ===================================================================
//  EVENT TYPE COLORS & LABELS
// ===================================================================

const eventTypeColors: Record<number, { bg: string; text: string }> = {
  1: { bg: "#EBF5FB", text: dt.blueHover },     // New Offer Received — blue
  2: { bg: "#F4F6F9", text: "#6B7280" },     // Offer Viewed — gray
  3: { bg: "#EAFAF1", text: "#27AE60" },     // Offer Accepted — green
  4: { bg: "#F9EBEA", text: "#C0392B" },     // Offer Rejected — red
  5: { bg: "#FEF9E7", text: "#F39C12" },     // Expiring Soon — orange
  6: { bg: "#F5EEF8", text: "#8E44AD" },     // Offer Withdrawn — violet
};

const allEventTypeCodes = [1, 2, 3, 4, 5, 6];

// ===================================================================
//  EventTypeBadge — left-bar accent style
// ===================================================================

interface EventTypeBadgeProps {
  eventType: number;
  label: string;
}

const EventTypeBadge = ({ eventType, label }: EventTypeBadgeProps) => {
  const c = eventTypeColors[eventType] || eventTypeColors[2];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: c.bg,
        borderLeft: `3px solid ${c.text}`,
        borderRadius: "0 8px 8px 0",
        padding: "5px 14px 5px 10px",
        fontSize: { xs: "11px", sm: "12px" },
        fontWeight: 600,
        fontFamily: dt.font,
        color: c.text,
        letterSpacing: "0.2px",
        whiteSpace: "nowrap",
        lineHeight: "18px",
        transition: "all 0.2s ease",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </Box>
  );
};

// ===================================================================
//  MobileNotificationCard
// ===================================================================

interface MobileNotificationCardProps {
  notification: AppNotification;
  translate: (key: string) => string;
  eventTypeLabelMap: Record<number, string>;
  onClick: (notification: AppNotification) => void;
  onMarkAsRead: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  index: number;
}

const MobileNotificationCard = ({
  notification,
  translate,
  eventTypeLabelMap,
  onClick,
  onMarkAsRead,
  onDelete,
  index,
}: MobileNotificationCardProps) => {
  const c = eventTypeColors[notification.eventType] || eventTypeColors[2];

  const formatDate = (dateStr: string) => {
    const date = dayjs(dateStr);
    const now = dayjs();
    const diffHours = now.diff(date, "hour");
    if (diffHours < 24) return date.fromNow();
    return date.format("DD MMM, YYYY HH:mm");
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead(notification.id);
  };

  return (
    <Box
      onClick={() => onClick(notification)}
      sx={{
        backgroundColor: notification.isRead ? dt.cardBg : "#FAFCFF",
        borderRadius: dt.radiusSm,
        border: `1px solid ${notification.isRead ? dt.cardBorder : "#D4E6F7"}`,
        borderLeft: `3px solid ${notification.isRead ? dt.cardBorder : c.text}`,
        padding: "12px",
        cursor: "pointer",
        boxShadow: dt.cardShadow,
        overflow: "hidden",
        width: "100%",
        boxSizing: "border-box",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
        "@keyframes cardReveal": {
          "0%": { opacity: 0, transform: "translateY(16px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Header: Unread dot + Event Type badge */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {!notification.isRead && (
            <DotIcon sx={{ fontSize: "10px", color: dt.blue }} />
          )}
          <EventTypeBadge
            eventType={notification.eventType}
            label={eventTypeLabelMap[notification.eventType] || "Unknown"}
          />
        </Box>
        {!notification.isRead && (
          <Tooltip title={translate("common:notifications.history.markAsRead")}>
            <IconButton
              size="small"
              onClick={handleMarkAsRead}
              sx={{
                width: "28px",
                height: "28px",
                borderRadius: "8px",
                color: dt.textMuted,
                transition: "all 0.2s",
                "&:hover": { backgroundColor: dt.blueGlow, color: dt.blue },
              }}
            >
              <VisibilityOutlined sx={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ mb: 1, overflow: "hidden" }}>
        <Box
          sx={{
            color: dt.textPrimary,
            fontWeight: notification.isRead ? 400 : 600,
            fontSize: "13px",
            fontFamily: dt.font,
            mb: 0.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {notification.title}
        </Box>
        <Box
          sx={{
            color: dt.textSecondary,
            fontSize: "12px",
            fontFamily: dt.font,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {notification.message}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 1,
          borderTop: `1px solid ${dt.rowBorder}`,
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            color: dt.textMuted,
            fontSize: "11px",
            fontFamily: dt.fontMono,
            letterSpacing: "0.2px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {formatDate(notification.createdDate)}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          <PermissionGuard module={Modules.Notifications} level={PermissionLevel.Admin}>
            <Tooltip title={translate("common:notifications.history.delete")}>
              <IconButton
                size="small"
                onClick={(e) => onDelete(e, notification.id)}
                sx={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "6px",
                  color: dt.textMuted,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "rgba(192, 57, 43, 0.08)",
                    color: "#C0392B",
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>
          </PermissionGuard>
          <ChevronRightIcon sx={{ color: dt.textMuted, fontSize: "18px" }} />
        </Box>
      </Box>
    </Box>
  );
};

// ===================================================================
//  FilterItem — clickable row for sidebar & accordion
// ===================================================================

interface FilterItemProps {
  label: string;
  isSelected: boolean;
  statusColor?: string;
  indent?: boolean;
  onClick: () => void;
}

const FilterItem = ({ label, isSelected, statusColor, indent, onClick }: FilterItemProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 10px",
      paddingLeft: indent ? "14px" : "10px",
      marginLeft: indent ? "8px" : 0,
      borderRadius: dt.radiusSm,
      cursor: "pointer",
      transition: "all 0.2s ease",
      backgroundColor: isSelected
        ? statusColor
          ? `${statusColor}08`
          : dt.blueSoft
        : "transparent",
      borderLeft:
        isSelected && statusColor
          ? `3px solid ${statusColor}`
          : "3px solid transparent",
      "&:hover": {
        backgroundColor: isSelected
          ? statusColor
            ? `${statusColor}10`
            : dt.blueGlow
          : "#F8FAFC",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        fontFamily: dt.font,
        fontWeight: isSelected ? 600 : 400,
        color: isSelected ? statusColor || dt.blue : "#4B5563",
        letterSpacing: "0.1px",
      }}
    >
      {statusColor && (
        <Box
          sx={{
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: statusColor,
            flexShrink: 0,
            opacity: isSelected ? 1 : 0.5,
            transition: "opacity 0.2s",
          }}
        />
      )}
      {label}
    </Box>
    {isSelected && (
      <Box
        sx={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: statusColor || dt.blue,
          flexShrink: 0,
        }}
      />
    )}
  </Box>
);

// ===================================================================
//  PAGINATION WRAPPER -- unified styling
// ===================================================================

const paginationWrapperSx = {
  "& *": { fontFamily: `${dt.font} !important` },
  "& span, & p, & div": { fontSize: "13px !important", letterSpacing: "0.1px" },
  "& .MuiIconButton-root": {
    color: dt.textSecondary,
    borderRadius: dt.radiusSm,
    transition: "all 0.2s",
    "&:hover": { backgroundColor: dt.blueGlow, color: dt.blue },
  },
};

// ===================================================================
//  MAIN COMPONENT
// ===================================================================

const NotificationHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation("common");
  const dispatch = useAppDispatch();

  const { query, notifications, loading, error } =
    useAppSelector<NotificationHistoryState>(
      (state: any) => state.notificationHistory
    );

  // 3-tier responsive: Desktop > 1200, Tablet 768-1200, Phone < 768
  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(query.search || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  // --- Event type label map ---
  const eventTypeLabelMap: Record<number, string> = useMemo(
    () => ({
      1: translate("common:notifications.eventTypes.newOfferReceived"),
      2: translate("common:notifications.eventTypes.offerViewed"),
      3: translate("common:notifications.eventTypes.offerAccepted"),
      4: translate("common:notifications.eventTypes.offerRejected"),
      5: translate("common:notifications.eventTypes.offerExpiringSoon"),
      6: translate("common:notifications.eventTypes.offerWithdrawn"),
    }),
    [translate]
  );

  // --- Inject keyframes ---
  useEffect(() => {
    const styleId = "notif-history-redesign-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeLeft {
          0% { opacity: 0; transform: translateX(-12px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes headerReveal {
          0% { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ===================================================================
  //  EVENT HANDLERS -- logic preserved exactly
  // ===================================================================

  useEffect(() => {
    const initialQuery: NotificationHistoryQuery = {
      pageNo: 1,
      pageSize: 10,
      sortDesc: true,
    };
    dispatch(changeHistoryQuery(initialQuery));
    dispatch(fetchNotificationHistory(initialQuery));
  }, [dispatch]);

  // Keep a ref to the current query for use in real-time refresh callbacks
  const queryRef = useRef(query);
  queryRef.current = query;

  // RT-5.3: Real-time auto-update when new notifications arrive
  useDataRefresh("notifications", () => {
    dispatch(fetchNotificationHistory(queryRef.current));
    dispatch(fetchUnreadCount());
  });

  // Fallback: refresh data when tab becomes visible after being hidden >60s
  useVisibilityRefresh(() => {
    dispatch(fetchNotificationHistory(queryRef.current));
    dispatch(fetchUnreadCount());
  }, 60000);

  const dispatchQuery = (newQuery: NotificationHistoryQuery) => {
    dispatch(changeHistoryQuery(newQuery));
    dispatch(fetchNotificationHistory(newQuery));
  };

  const handleSearch = useCallback(
    (value?: string) => {
      dispatchQuery({ ...query, search: value, pageNo: 1 });
    },
    [query]
  );

  const handleSort = (parameter: number) => {
    const currentSortBy = query.sortBy;
    const currentSortDesc = query.sortDesc ?? true;
    const sortDesc = currentSortBy !== parameter ? true : !currentSortDesc;
    dispatchQuery({
      ...query,
      sortBy: parameter as NotificationHistorySortParameter,
      sortDesc,
      pageNo: 1,
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    dispatchQuery({ ...query, pageNo: 1, pageSize: newPageSize });
  };

  const handlePageNoChange = (newPage: number) => {
    dispatchQuery({ ...query, pageNo: newPage });
  };

  const handleEventTypesChange = (eventTypes: number[]) => {
    dispatchQuery({
      ...query,
      eventTypes: eventTypes.length > 0 ? eventTypes : undefined,
      pageNo: 1,
    });
  };

  const handleReadFilterChange = (filter: ReadFilter) => {
    dispatchQuery({ ...query, readFilter: filter, pageNo: 1 });
  };

  const handleDateFromChange = (value: Dayjs | null) => {
    dispatchQuery({
      ...query,
      dateFrom: value ? value.toISOString() : undefined,
      pageNo: 1,
    });
  };

  const handleDateToChange = (value: Dayjs | null) => {
    dispatchQuery({
      ...query,
      dateTo: value ? value.toISOString() : undefined,
      pageNo: 1,
    });
  };

  const handleClearAllFilters = () => {
    const clearedQuery: NotificationHistoryQuery = {
      pageNo: 1,
      pageSize: query.pageSize,
      sortDesc: true,
    };
    setSearchValue("");
    dispatchQuery(clearedQuery);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await dispatch(markHistoryNotificationAsRead(notificationId));
    dispatch(fetchUnreadCount());
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllHistoryAsRead());
    dispatch(fetchUnreadCount());
  };

  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    await dispatch(deleteHistoryNotification(notificationId));
    dispatch(fetchUnreadCount());
  };

  const handleRowClick = async (notification: AppNotification) => {
    if (!notification.isRead) {
      await dispatch(markAsRead(notification.id));
      dispatch(markHistoryNotificationAsRead(notification.id));
    }
    if (notification.linkUrl) {
      navigate(notification.linkUrl);
    }
  };

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        handleSearch(undefined);
        return;
      }
      debounceRef.current = setTimeout(() => {
        handleSearch(value || undefined);
      }, 400);
    },
    [handleSearch]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        handleSearch(searchValue || undefined);
      }
    },
    [searchValue, handleSearch]
  );

  const handleEventTypeToggle = useCallback(
    (code: number) => {
      const selected = query.eventTypes || [];
      if (selected.includes(code)) {
        handleEventTypesChange(selected.filter((c) => c !== code));
      } else {
        handleEventTypesChange([...selected, code]);
      }
    },
    [query.eventTypes]
  );

  const handleAllEventTypesToggle = useCallback(() => {
    const selected = query.eventTypes || [];
    const isAllSelected =
      allEventTypeCodes.length > 0 &&
      allEventTypeCodes.every((code) => selected.includes(code));
    if (isAllSelected) {
      handleEventTypesChange([]);
    } else {
      handleEventTypesChange([...allEventTypeCodes]);
    }
  }, [query.eventTypes]);

  // --- Derived state ---
  const hasUnread = notifications?.items?.some((n) => !n.isRead) ?? false;

  const formatDate = (dateStr: string) => {
    const date = dayjs(dateStr);
    const now = dayjs();
    const diffHours = now.diff(date, "hour");
    if (diffHours < 24) return date.fromNow();
    return date.format("DD MMM, YYYY HH:mm");
  };

  // --- Active filter count for badge ---
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (query.eventTypes && query.eventTypes.length > 0) count += query.eventTypes.length;
    if (query.readFilter && query.readFilter !== "all") count += 1;
    if (query.dateFrom) count += 1;
    if (query.dateTo) count += 1;
    return count;
  }, [query]);

  // ===================================================================
  //  COLUMN DEFINITIONS
  // ===================================================================

  const columns: TableColumnDefinition[] = [
    {
      title: "",
      columnAlign: "center",
      twoLevelsColumWidth: "40px",
    },
    {
      title: translate("common:notifications.history.columns.eventType"),
      columnAlign: "left",
      twoLevelsColumWidth: "180px",
    },
    {
      title: translate("common:notifications.history.columns.notification"),
      columnAlign: "left",
    },
    {
      title: translate("common:notifications.history.columns.date"),
      parameter: NotificationHistorySortParameter.createdDate,
      columnAlign: "left",
      twoLevelsColumWidth: "180px",
    },
    {
      title: translate("common:notifications.history.columns.actions"),
      columnAlign: "center",
      twoLevelsColumWidth: "100px",
    },
  ];

  if (loading && !notifications) {
    return <Loader />;
  }

  // ===================================================================
  //  Shared: inline filter content (used in sidebar & accordion)
  // ===================================================================

  const renderFilterContent = () => {
    const selectedEventTypes = query.eventTypes || [];
    const isAllSelected =
      allEventTypeCodes.length > 0 &&
      allEventTypeCodes.every((code) => selectedEventTypes.includes(code));

    return (
      <>
        {/* Search input */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            background: dt.inputBg,
            borderRadius: dt.radiusSm,
            border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
            padding: "0 12px",
            height: "40px",
            transition: "all 0.25s ease",
            boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
            mb: "16px",
          }}
        >
          <SearchOutlined
            sx={{
              fontSize: "16px",
              color: searchFocused ? dt.blue : dt.textMuted,
              flexShrink: 0,
              transition: "color 0.2s",
            }}
          />
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder={translate("common:notifications.history.searchPlaceholder")}
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              fontSize: "13px",
              fontFamily: 'var(--font-sans)',
              color: dt.textPrimary,
              background: "transparent",
              marginLeft: "8px",
            }}
          />
        </Box>

        {/* Date range section -- always under search */}
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            color: dt.textMuted,
            fontFamily: dt.font,
            mb: "10px",
            px: "4px",
          }}
        >
          {translate("common:notifications.history.dateRange")}
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: "4px" }}>
            <DatePicker
              label={translate("common:notifications.history.dateFrom")}
              value={query.dateFrom ? dayjs(query.dateFrom) : null}
              onChange={handleDateFromChange}
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      fontFamily: dt.fontMono,
                      fontSize: "13px",
                      borderRadius: dt.radiusSm,
                      backgroundColor: dt.inputBg,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: dt.cardBorder },
                      "&.Mui-focused fieldset": {
                        borderColor: dt.blue,
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontFamily: dt.font,
                      fontSize: "12px",
                    },
                  },
                },
              }}
            />
            <DatePicker
              label={translate("common:notifications.history.dateTo")}
              value={query.dateTo ? dayjs(query.dateTo) : null}
              onChange={handleDateToChange}
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      fontFamily: dt.fontMono,
                      fontSize: "13px",
                      borderRadius: dt.radiusSm,
                      backgroundColor: dt.inputBg,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: dt.cardBorder },
                      "&.Mui-focused fieldset": {
                        borderColor: dt.blue,
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontFamily: dt.font,
                      fontSize: "12px",
                    },
                  },
                },
              }}
            />
          </Box>
        </LocalizationProvider>

        {/* Divider */}
        <Box sx={{ borderTop: `1px dashed ${dt.cardBorder}`, my: "12px", mx: "4px" }} />

        {/* Filter header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "12px",
            px: "4px",
          }}
        >
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              color: dt.textMuted,
              fontFamily: dt.font,
            }}
          >
            {translate("common:notifications.history.eventType")}
          </Box>
          {activeFilterCount > 0 && (
            <Box
              onClick={handleClearAllFilters}
              sx={{
                fontSize: "12px",
                color: dt.blue,
                cursor: "pointer",
                fontWeight: 500,
                fontFamily: dt.font,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {translate("common:notifications.history.clearAll")}
            </Box>
          )}
        </Box>

        {/* All event types */}
        <FilterItem
          label={translate("common:notifications.history.allEventTypes")}
          isSelected={isAllSelected}
          onClick={handleAllEventTypesToggle}
        />

        {/* Individual event types */}
        {allEventTypeCodes.map((code) => (
          <FilterItem
            key={code}
            label={eventTypeLabelMap[code]}
            isSelected={selectedEventTypes.includes(code)}
            statusColor={eventTypeColors[code]?.text}
            indent
            onClick={() => handleEventTypeToggle(code)}
          />
        ))}

        {/* Separator */}
        <Box sx={{ borderTop: `1px dashed ${dt.cardBorder}`, my: "12px", mx: "4px" }} />

        {/* Read status section */}
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            color: dt.textMuted,
            fontFamily: dt.font,
            mb: "8px",
            px: "4px",
          }}
        >
          {translate("common:notifications.history.readStatus")}
        </Box>

        <RadioGroup
          value={query.readFilter || "all"}
          onChange={(e) => handleReadFilterChange(e.target.value as ReadFilter)}
          sx={{ gap: "2px", px: "4px" }}
        >
          {(["all", "unread", "read"] as ReadFilter[]).map((filterValue) => (
            <FormControlLabel
              key={filterValue}
              value={filterValue}
              control={
                <Radio
                  size="small"
                  sx={{
                    color: dt.textMuted,
                    "&.Mui-checked": { color: dt.blue },
                    padding: "4px",
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    fontSize: "13px",
                    fontFamily: dt.font,
                    color:
                      (query.readFilter || "all") === filterValue
                        ? dt.textPrimary
                        : dt.textSecondary,
                    fontWeight:
                      (query.readFilter || "all") === filterValue ? 500 : 400,
                  }}
                >
                  {translate(
                    `common:notifications.history.readStatus${
                      filterValue.charAt(0).toUpperCase() + filterValue.slice(1)
                    }`
                  )}
                </Box>
              }
              sx={{ margin: 0, ml: "-2px" }}
            />
          ))}
        </RadioGroup>

      </>
    );
  };

  // ===================================================================
  //  Shared: table view (used for desktop & tablet)
  // ===================================================================

  const renderTable = () => (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusLg,
        border: `1px solid ${dt.cardBorder}`,
        overflowX: "hidden",
        boxShadow: dt.cardShadow,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer>
        <Table
          sx={{
            "& thead th": {
              backgroundColor: dt.headerBg,
              borderBottom: `1px solid ${dt.cardBorder}`,
              fontSize: "11px !important",
              fontWeight: "700 !important",
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              color: "#64748B !important",
              fontFamily: `${dt.font} !important`,
              padding: "16px 18px !important",
              whiteSpace: "nowrap",
            },
            "& thead .MuiTableSortLabel-root": {
              fontSize: "11px !important",
              fontWeight: "700 !important",
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              color: "#64748B !important",
              fontFamily: `${dt.font} !important`,
            },
            "& thead .MuiTableSortLabel-root.Mui-active": {
              color: `${dt.blue} !important`,
            },
            "& thead .MuiTableSortLabel-icon": { color: "#9CA3AF !important" },
            "& thead th:first-of-type": { paddingLeft: "24px" },
            "& tbody td:first-of-type": { paddingLeft: "24px" },
            "& tbody td": {
              padding: "18px 18px !important",
              fontSize: "14px",
              fontFamily: `${dt.font} !important`,
              borderBottom: `1px solid ${dt.rowBorder}`,
              lineHeight: "1.5",
            },
            "& tbody tr": {
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: dt.rowHover,
                boxShadow: `inset 3px 0 0 0 ${dt.blue}`,
              },
            },
            "& tbody tr:last-child td": { borderBottom: "none" },
          }}
        >
          <SortTableHead
            columns={columns}
            onSort={handleSort}
            sortBy={query.sortBy}
            sortDesc={query.sortDesc}
          />
          <TableBody>
            {notifications?.items?.map((notification) => (
              <TableRow
                key={notification.id}
                onClick={() => handleRowClick(notification)}
                sx={{
                  backgroundColor: notification.isRead
                    ? "transparent"
                    : dt.blueSoft,
                }}
              >
                {/* Unread indicator */}
                <TableCell sx={{ width: "40px", textAlign: "center" }}>
                  {!notification.isRead && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: dt.blue,
                        margin: "0 auto",
                        boxShadow: `0 0 6px ${dt.blueGlow}`,
                        animation: "pulse 2s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%, 100%": {
                            boxShadow: `0 0 4px ${dt.blueGlow}`,
                          },
                          "50%": {
                            boxShadow: `0 0 8px ${dt.blueGlow}, 0 0 16px ${dt.blueGlow}`,
                          },
                        },
                      }}
                    />
                  )}
                </TableCell>

                {/* Event Type badge */}
                <TableCell sx={{ width: "180px" }}>
                  <EventTypeBadge
                    eventType={notification.eventType}
                    label={
                      eventTypeLabelMap[notification.eventType] || "Unknown"
                    }
                  />
                </TableCell>

                {/* Title & Message */}
                <TableCell>
                  <Box
                    sx={{
                      color: dt.textPrimary,
                      fontWeight: notification.isRead ? 400 : 600,
                      fontSize: "14px",
                      fontFamily: dt.font,
                      mb: "4px",
                      lineHeight: "1.4",
                    }}
                  >
                    {notification.title}
                  </Box>
                  <Tooltip
                    title={notification.message || ""}
                    enterDelay={400}
                    arrow
                  >
                    <Box
                      sx={{
                        color: dt.textSecondary,
                        fontSize: "12px",
                        fontFamily: dt.font,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "400px",
                      }}
                    >
                      {notification.message}
                    </Box>
                  </Tooltip>
                </TableCell>

                {/* Date */}
                <TableCell sx={{ width: "180px" }}>
                  <Box
                    sx={{
                      color: dt.textMuted,
                      fontSize: "12px",
                      fontFamily: dt.fontMono,
                      letterSpacing: "0.3px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDate(notification.createdDate)}
                  </Box>
                </TableCell>

                {/* Actions */}
                <TableCell sx={{ width: "100px", textAlign: "center" }}>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: "4px" }}>
                    {!notification.isRead && (
                      <Tooltip
                        title={translate(
                          "common:notifications.history.markAsRead"
                        )}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            color: dt.textMuted,
                            transition: "all 0.2s",
                            "&:hover": {
                              backgroundColor: dt.blueGlow,
                              color: dt.blue,
                            },
                          }}
                        >
                          <VisibilityOutlined sx={{ fontSize: "18px" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <PermissionGuard module={Modules.Notifications} level={PermissionLevel.Admin}>
                      <Tooltip
                        title={translate(
                          "common:notifications.history.delete"
                        )}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(e, notification.id)}
                          sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            color: dt.textMuted,
                            transition: "all 0.2s",
                            "&:hover": {
                              backgroundColor: "rgba(192, 57, 43, 0.08)",
                              color: "#C0392B",
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: "18px" }} />
                        </IconButton>
                      </Tooltip>
                    </PermissionGuard>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          "& > div": {
            borderTop: `1px solid ${dt.rowBorder}`,
            borderRadius: 0,
            padding: "6px 18px",
            backgroundColor: dt.headerBg,
          },
          ...paginationWrapperSx,
        }}
      >
        <LightTablePagination
          totalCount={notifications?.totalCount ?? 0}
          currentPage={query.pageNo}
          pageSize={query.pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageChange={handlePageNoChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageLabel={translate("common:notifications.history.rowsPerPage")}
          ofLabel={translate("common:notifications.history.of")}
        />
      </Box>
    </Box>
  );

  // ===================================================================
  //  RENDER
  // ===================================================================

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        minHeight: "100%",
        fontFamily: dt.font,
        overflowX: "hidden",
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      <ActionToast
        open={!!error}
        variant="error"
        message={translate(NotificationMessage.error(error).message)}
        onClose={() => dispatch(clearHistoryError())}
        duration={6000}
      />

      {/* ===================================================================
          PAGE HEADER
          =================================================================== */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: { xs: 1.5, md: 2 },
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: 2,
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontSize: { xs: "20px", sm: "22px", md: "28px" },
              fontWeight: 800,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "-0.5px",
              lineHeight: 1.2,
            }}
          >
            {translate("common:notifications.history.pageTitle")}
          </Box>
          <Box
            sx={{
              width: "48px",
              height: "3px",
              borderRadius: "2px",
              background: `linear-gradient(90deg, ${dt.blue}, ${dt.blue}00)`,
              mt: 1,
            }}
          />
        </Box>

        {/* Mark all as read button */}
        {hasUnread && (
          <Button
            variant="contained"
            startIcon={<DoneAllOutlined sx={{ fontSize: "16px !important" }} />}
            onClick={handleMarkAllAsRead}
            sx={{
              background: accentGradientDark,
              borderRadius: dt.radiusPill,
              padding: isPhone ? "7px 16px" : { xs: "8px 18px", md: "11px 24px" },
              textTransform: "none",
              fontWeight: 600,
              fontSize: isPhone ? "12px" : { xs: "13px", md: "14px" },
              fontFamily: dt.font,
              letterSpacing: "0.2px",
              boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
              border: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              minWidth: "auto",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: accentGradientDarkHover,
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
              },
            }}
          >
            {translate("common:notifications.markAllAsRead")}
          </Button>
        )}
      </Box>

      {/* ===================================================================
          MAIN CONTENT
          =================================================================== */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          px: { xs: 2, md: 3 },
          pb: 3,
          flex: 1,
          alignItems: "flex-start",
          minWidth: 0,
          overflowX: "hidden",
        }}
      >
        {/* =================================================================
            DESKTOP SIDEBAR (>1200px only)
            ================================================================= */}
        {!isCompact && (
          <Box
            sx={{
              width: "260px",
              minWidth: "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              animation:
                "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: "22px",
                boxShadow: dt.cardShadow,
              }}
            >
              {renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* =================================================================
            MAIN AREA (table or cards)
            ================================================================= */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
            animation:
              "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
          }}
        >
          {/* =================================================================
              ACCORDION FILTERS (tablet & phone -- compact mode)
              ================================================================= */}
          {isCompact && (
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded(!filtersExpanded)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: dt.textSecondary }} />
                }
                sx={{
                  minHeight: "48px",
                  px: 2,
                  "&.Mui-expanded": { minHeight: "48px" },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 1,
                    my: "10px",
                    "&.Mui-expanded": { my: "10px" },
                  },
                }}
              >
                <FilterListIcon
                  sx={{ color: dt.textMuted, fontSize: "20px" }}
                />
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: dt.textSecondary,
                    fontFamily: dt.font,
                  }}
                >
                  {translate("common:notifications.history.filterBy")}
                </Box>
                {activeFilterCount > 0 && (
                  <Badge
                    badgeContent={activeFilterCount}
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        backgroundColor: dt.blue,
                        color: "#fff",
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {renderFilterContent()}
              </AccordionDetails>
            </Accordion>
          )}

          {/* =================================================================
              TABLE VIEW (desktop + tablet, i.e. not phone)
              ================================================================= */}
          {!isPhone && (
            <>
              {loading && !notifications?.items ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "400px",
                    flex: 1,
                  }}
                >
                  <Loader />
                </Box>
              ) : notifications?.items && notifications.items.length > 0 ? (
                renderTable()
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "400px",
                    flex: 1,
                  }}
                >
                  <TableEmpty
                    iconComponent={NotificationsNoneOutlined}
                    text={translate("common:notifications.history.noResults")}
                    secondaryText={translate(
                      "common:notifications.history.noResultsHint"
                    )}
                  />
                </Box>
              )}
            </>
          )}

          {/* =================================================================
              CARD VIEW (phone only)
              ================================================================= */}
          {isPhone && (
            <>
              {loading && !notifications?.items ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "300px",
                    flex: 1,
                  }}
                >
                  <Loader />
                </Box>
              ) : notifications?.items && notifications.items.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    overflowX: "hidden",
                    width: "100%",
                  }}
                >
                  {notifications.items.map((notification, index) => (
                    <MobileNotificationCard
                      key={notification.id}
                      notification={notification}
                      translate={translate}
                      eventTypeLabelMap={eventTypeLabelMap}
                      onClick={handleRowClick}
                      onMarkAsRead={handleMarkAsRead}
                      onDelete={handleDelete}
                      index={index}
                    />
                  ))}
                  <Box sx={{ mt: 1, ...paginationWrapperSx }}>
                    <LightTablePagination
                      totalCount={notifications?.totalCount ?? 0}
                      currentPage={query.pageNo}
                      pageSize={query.pageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onPageChange={handlePageNoChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageLabel={translate(
                        "common:notifications.history.rowsPerPage"
                      )}
                      ofLabel={translate("common:notifications.history.of")}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radiusLg,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    minHeight: "300px",
                    flex: 1,
                  }}
                >
                  <TableEmpty
                    iconComponent={NotificationsNoneOutlined}
                    text={translate("common:notifications.history.noResults")}
                    secondaryText={translate(
                      "common:notifications.history.noResultsHint"
                    )}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationHistoryPage;
